/* eslint-disable max-len */
import { INVITE_MSG, INVITE_AND_EARN } from '../../utils/constants';
import { referralData, rewardData, logEventData } from './interface';
import { EventNamesV2, PAGE_NAMES } from '../../utils/events/eventsConstants';
import {
  ALERT_BLACK_ICON,
  CIRCLE_CHECK_BLACK_ICON,
  INFORMATION_ICON_BLACK_BOLD_ICON,
  QUESTION_MARK_ICON,
  ROUND_CROSS_ICON,
  TIME_ICON
} from '@Images';
import { logEventV2 } from '@Utils/events/logUserEvents';

const referralFallbackData = {
  referralRevamp: {
    shareData: {
      heading: 'Share referral link',
      tncLink: '',
      shareTitle: 'Invitation Link',
      shareMessage: INVITE_MSG,
      shareSubject: INVITE_AND_EARN,
      termsConditionText: 'Terms & Conditions',
      termsApply: 'Terms & Conditions',
      termsReadMoreText: 'Read more about our referral'
    },
    pageHeading: 'Referral',
    webPageTitle: 'Invite & earn',
    heading: 'It’s a win-win!',
    description: 'Refer a friend to get rewarded',
    promotionSubText: 'Valid if referral is completed by {date}',
    howItWork: 'How it works?',
    referralActivitySection: {
      heading: 'Referral activity',
      pending: 'Pending',
      completed: 'Completed',
      all: 'All',
      codeUsed: 'Code used',
      joined: 'Joined',
      noReferralFound:
        "No referrals yet? Let's change that! Invite your friends and enjoy the perks together.",
      sendReminder: 'Send reminder',
      shareMessage: {
        message:
          "Hi {REFEREES_NAME},\n\nThank you for signing up with Instarem using my referral code!🎉 I'm excited for you to experience fast and seamless money transfers.\n\nJust a quick reminder to complete your account verification so you can start using all the features. Plus, you’ll receive an exclusive discount on your first transaction!\n\nIf you have any questions or need any help, feel free to reach out—I’d be happy to assist!\n\nLooking forward to seeing you enjoy Instarem.😊\n\nBest,\n{REMITTER_NAME}",
        emailSubject: encodeURIComponent('Complete Your Instarem Account & Enjoy Your Reward! 🎉')
      }
    },
    instapointsSection: {
      heading: 'InstaPoints',
      headingRight: 'Redeem'
    },
    referQRCodeModalText: {
      heading: 'Refer a friend and earn!',
      description: 'Ask your friend to scan this QR code to use your referral'
    },
    referralWorksModal: {
      heading: 'Refer a friend and earn',
      description: 'Refer your friends and get rewarded! Here’s how:',
      gotItBtnText: 'Got it',
      referralWorkSteps: [
        'Share your referral link with your friends.',
        'They sign up and complete their first transfer with us.',
        'You receive your reward via email.'
      ]
    },
    defaultRewards: {
      youGet: 'You get',
      worth: 'Worth',
      theyGet: 'They get',
      ipValue: '{INITIAL_IP_VALUE} InstaPoints',
      ipRedeemValue: '{INITIAL_IP_REDEEM_VALUE} {CURRENCY}',
      couponName: 'Amazon voucher',
      couponValue: '{TRANSACTIONAL_IP_REDEEM_VALUE} {CURRENCY}'
    },
    promotionRewards: {
      worth: 'Worth',
      youGet: 'You get',
      ipValue: '200 InstaPoints',
      theyGet: 'They get',
      couponName: 'Amazon voucher',
      couponValue: '20 {CURRENCY}',
      ipRedeemValue: '2.5 {CURRENCY}'
    },
    uniformReferralReward: {
      youGet: 'You get',
      worth: 'Worth',
      theyGet: 'They get',
      ipValue: 'Bonus',
      ipRedeemValue: '{INITIAL_IP_REDEEM_VALUE} {CURRENCY}',
      couponName: 'Bonus',
      couponValue: '{TRANSACTIONAL_IP_REDEEM_VALUE} {CURRENCY}'
    },
    importantInfo: {
      title: 'Important information',
      config: [
        {
          identifier: 'POSITIVE',
          icon: CIRCLE_CHECK_BLACK_ICON,
          message: 'Send a transfer of {MIN_TRANSFER_AMOUNT} {CURRENCY} or more, OR'
        },
        {
          identifier: 'NEGATIVE',
          icon: ROUND_CROSS_ICON,
          message: 'Spend a cumulative total of {MIN_TRANSFER_AMOUNT} {CURRENCY} or more on amaze.'
        }
      ]
    },
    uniformReferralImportantInfo: {
      title: 'Important information',
      config: [
        {
          identifier: 'POSITIVE',
          icon: CIRCLE_CHECK_BLACK_ICON,
          message:
            'Your friend must successfully complete their transfer of at least {MIN_TRANSFER_AMOUNT} by 30 June 2026.'
        },
        {
          identifier: 'POSITIVE',
          icon: CIRCLE_CHECK_BLACK_ICON,
          message: 'Once done, your referral coupon will be sent to your email.'
        },
        {
          identifier: 'NEGATIVE',
          icon: ROUND_CROSS_ICON,
          message: "Same-currency transfers don't count."
        }
      ]
    }
  }
};

const getIcon = (item: { identifier?: string; icon?: string }) => {
  if (item?.identifier === 'NEGATIVE') return ROUND_CROSS_ICON;
  if (item?.identifier === 'POSITIVE') return CIRCLE_CHECK_BLACK_ICON;
  return item?.icon ? getMappedIconFromCodebase(item.icon) : '';
};

const getMappedIconFromCodebase = (iconName: string) => {
  let icon;
  switch (iconName) {
    case 'timeclock':
      icon = TIME_ICON;
      break;
    case 'alert-circle':
      icon = INFORMATION_ICON_BLACK_BOLD_ICON;
      break;
    case 'help-circle':
      icon = QUESTION_MARK_ICON;
      break;
    case 'info':
      icon = ALERT_BLACK_ICON;
      break;
    case 'x-circle':
      icon = ROUND_CROSS_ICON;
      break;
    default:
      icon = QUESTION_MARK_ICON;
  }
  return icon;
};

const getImportantInfoData = (
  config: Array<{ identifier?: string; icon?: string; message?: string; showDot?: boolean }> = []
) => {
  const fallbackMessages = referralFallbackData.referralRevamp.importantInfo.config;
  const configData: Array<{ icon: string; message?: string; showDot?: boolean }> = [
    { icon: CIRCLE_CHECK_BLACK_ICON, message: fallbackMessages[0]?.message },
    { icon: ROUND_CROSS_ICON, message: fallbackMessages[1]?.message }
  ];
  if (Array.isArray(config)) {
    config.forEach((item, index) => {
      const icon = getIcon(item);
      // As per logic neutral entry can be added after 2 points.
      if (index < 2) {
        configData[index] = {
          icon: icon || configData[index].icon,
          message: item?.message || configData[index].message,
          showDot: item?.showDot
        };
      } else if (item?.message && icon) {
        configData.push({
          icon,
          message: item.message,
          showDot: item?.showDot
        });
      }
    });
  }
  return configData;
};

export const getReferralLabels = (
  data: { [key: string]: any },
  countryCode: string,
  isUniformReferralEnabled: boolean
): referralData => {
  const labels = data?.labelAndLinks;
  const inviteEarnRevampData = labels?.inviteEarnRevampData;
  const promotionText = inviteEarnRevampData?.promotionText;
  const shareData = labels?.shareData;
  const promotionRewardsInfo = inviteEarnRevampData?.promotionRewards?.importantInfo;
  const importantInfo =
    promotionRewardsInfo?.title && promotionRewardsInfo?.config?.length
      ? promotionRewardsInfo
      : inviteEarnRevampData?.defaultRewards?.importantInfo;
  const showInfo = isUniformReferralEnabled
    ? importantInfo?.revampConfig
    : importantInfo?.newConfig;
  const referralActivitySection = inviteEarnRevampData?.referralActivitySection;
  const instapointsSection = inviteEarnRevampData?.instapointsSection;
  const referQRCodeModalText = inviteEarnRevampData?.referQRCodeModalText;
  const referralWorksModal = inviteEarnRevampData?.referralWorksModal;
  const tncLink = `https://www.instarem.com/referral-bonus/`;
  const fallBackData = referralFallbackData?.referralRevamp;
  const fallBackShareData = fallBackData?.shareData;
  const fallBackActivityData = fallBackData?.referralActivitySection;
  const fallBackInstapointsData = fallBackData?.instapointsSection;
  const fallBackQrData = fallBackData?.referQRCodeModalText;
  const fallBackWorksData = fallBackData?.referralWorksModal;

  return {
    shareData: {
      heading: shareData?.heading || fallBackShareData?.heading,
      shareTitle: shareData?.shareTitle || fallBackShareData?.shareTitle,
      shareMessage: shareData?.shareMessage || fallBackShareData?.shareMessage,
      shareSubject: shareData?.shareSubject || fallBackShareData?.shareSubject,
      termsConditionText: shareData?.termsConditionText || fallBackShareData?.termsConditionText,
      termsReadMoreText: shareData?.termsReadMoreText || fallBackShareData?.termsReadMoreText,
      termsApply: shareData?.termsApply || fallBackShareData?.termsApply
    },
    pageHeading: inviteEarnRevampData?.pageHeading || fallBackData?.pageHeading,
    webPageTitle: labels?.webPageTitle || fallBackData?.webPageTitle,
    heading: inviteEarnRevampData?.heading || fallBackData?.heading,
    promotionSubText: inviteEarnRevampData?.promotionSubText,
    howItWork: inviteEarnRevampData?.howItWork || fallBackData?.howItWork,
    description: inviteEarnRevampData?.description || fallBackData?.description,
    tncLink: inviteEarnRevampData?.tncLink?.replace('{COUNTRY_CODE}', countryCode) || tncLink,
    referralActivitySection: {
      heading: referralActivitySection?.heading || fallBackActivityData?.heading,
      pending: referralActivitySection?.pending || fallBackActivityData?.pending,
      completed: referralActivitySection?.completed || fallBackActivityData?.completed,
      all: referralActivitySection?.all || fallBackActivityData?.all,
      codeUsed: referralActivitySection?.codeUsed || fallBackActivityData?.codeUsed,
      joined: referralActivitySection?.joined || fallBackActivityData?.joined,
      noReferralFound:
        referralActivitySection?.noReferralFound || fallBackActivityData?.noReferralFound,
      sendReminder: referralActivitySection?.sendReminder || fallBackActivityData?.sendReminder,
      shareMessage: referralActivitySection?.shareMessage || fallBackActivityData?.shareMessage
    },
    instapointsSection: {
      heading: instapointsSection?.heading || fallBackInstapointsData?.heading,
      headingRight: instapointsSection?.headingRight || fallBackInstapointsData?.headingRight
    },
    referQRCodeModalText: {
      heading: referQRCodeModalText?.heading || fallBackQrData?.heading,
      description: referQRCodeModalText?.description || fallBackQrData?.description
    },
    referralWorksModal: {
      heading: referralWorksModal?.heading || fallBackWorksData?.heading,
      description: referralWorksModal?.description || fallBackWorksData?.description,
      gotItBtnText: referralWorksModal?.gotItBtnText || fallBackWorksData?.gotItBtnText,
      referralWorkSteps:
        referralWorksModal?.referralWorkSteps || fallBackWorksData?.referralWorkSteps
    },
    promotionText: promotionText,
    importantInfo: {
      title: importantInfo?.title || referralFallbackData.referralRevamp.importantInfo.title,
      config: getImportantInfoData(showInfo)
    }
  };
};

export const getRewardData = (
  inviteEarnCMS: { [key: string]: any },
  currency: string,
  initialIPValue: string,
  initialIPRedeemValue: string,
  transactionalIPRedeemValue: string,
  transactionReferralInstapoint: number | undefined
): rewardData => {
  const data = inviteEarnCMS?.labelAndLinks;
  const labels = data?.inviteEarnRevampData;
  const defaultRewards = labels?.defaultRewards;
  const promotionRewards = labels?.promotionRewards;
  const fallbackdefaultRewards = referralFallbackData?.referralRevamp?.defaultRewards;
  const activeRewards = promotionRewards || defaultRewards || fallbackdefaultRewards;

  const replaceDescriptionText = (value: string) => {
    if (value?.includes('{INITIAL_IP_VALUE}')) {
      value = value?.replace('{INITIAL_IP_VALUE}', initialIPValue);
    }
    if (value?.includes('{CURRENCY}')) {
      value = value?.replace('{CURRENCY}', currency);
    }
    if (value?.includes('{TRANSACTIONAL_IP_REDEEM_VALUE}')) {
      value = value?.replace('{TRANSACTIONAL_IP_REDEEM_VALUE}', transactionalIPRedeemValue);
    }
    if (value?.includes('{INITIAL_IP_REDEEM_VALUE}')) {
      value = value?.replace('{INITIAL_IP_REDEEM_VALUE}', initialIPRedeemValue);
    }
    if (value?.includes('{TRANSACTIONAL_IP_VALUE}')) {
      value = value?.replace(
        '{TRANSACTIONAL_IP_VALUE}',
        transactionReferralInstapoint?.toString() || ''
      );
    }
    return value;
  };

  return {
    rewards: {
      worth: activeRewards?.worth || defaultRewards?.worth,
      youGet: activeRewards?.youGet || defaultRewards?.youGet,
      ipValue: replaceDescriptionText(activeRewards?.ipValue || defaultRewards?.ipValue),
      theyGet: activeRewards?.theyGet || defaultRewards?.theyGet,
      couponName: replaceDescriptionText(activeRewards?.couponName || defaultRewards?.couponName),
      couponValue: replaceDescriptionText(
        activeRewards?.couponValue || defaultRewards?.couponValue
      ),
      ipRedeemValue: replaceDescriptionText(
        activeRewards?.ipRedeemValue || defaultRewards?.ipRedeemValue
      )
    }
  };
};

export const logReferEvent = (eventName: string, shareType?: string): void => {
  let data: logEventData = {
    pageName: PAGE_NAMES.REFER_AND_EARN
  };
  if (shareType) {
    data = {
      pageName: PAGE_NAMES.REFER_AND_EARN,
      type: shareType
    };
  }
  logEventV2(EventNamesV2.SCREEN_VIEWED, data);
};
